package dev.kuml.portal.web.api

import kotlinx.serialization.Serializable

/**
 * Client-side mirror of `kuml-portal-server`'s `dev.kuml.portal.server.dto.*` DTOs -- field names
 * and nullability match the server exactly (see `kuml-portal-server/.../dto/Dtos.kt`). This module
 * is intentionally NOT wired to a shared `commonMain` (see the architecture decision in
 * `build.gradle.kts`), so this is a plain, hand-kept mirror rather than a generated/shared type.
 */

@Serializable
public data class RegisterRequest(
    val email: String,
    val password: String,
    val displayName: String,
    /** R-3/language question -- filled from `LanguageState.current.storageValue`. */
    val language: String? = null,
)

@Serializable
public data class LoginRequest(
    val email: String,
    val password: String,
)

@Serializable
public data class UserResponse(
    val id: String,
    val email: String,
    val displayName: String,
    val avatarUpdatedAt: String? = null,
    val trainingOptOut: Boolean = false,
    val showcaseOptIn: Boolean = false,
    /** R-3. `false` = show the verification hint banner. */
    val emailVerified: Boolean = true,
)

// ---------- Email infrastructure wave: R-3 / R-4 ----------

@Serializable
public data class VerifyEmailRequest(
    val token: String,
)

@Serializable
public data class ResendVerificationRequest(
    val language: String? = null,
)

@Serializable
public data class PasswordResetRequest(
    val email: String,
    val language: String? = null,
)

@Serializable
public data class PasswordResetConfirmRequest(
    val token: String,
    val password: String,
)

/** @property defaultDiagramId R-22: the id `/projects/:id` (no diagram segment) resolves to --
 * the project's OLDEST diagram. `null` only in the theoretically unreachable "project has zero
 * diagrams" case. */
@Serializable
public data class ProjectResponse(
    val id: String,
    val name: String,
    val description: String? = null,
    val createdAt: String,
    val updatedAt: String,
    val defaultDiagramId: String? = null,
)

@Serializable
public data class CreateProjectRequest(
    val name: String,
    val description: String? = null,
)

/** R-22: success body for `POST /api/projects` -- the freshly created project plus its
 * server-seeded initial diagram. */
@Serializable
public data class CreateProjectResponse(
    val project: ProjectResponse,
    val initialDiagram: DiagramResponse,
)

@Serializable
public data class DiagramResponse(
    val id: String,
    val projectId: String,
    val name: String,
    val createdAt: String,
    val updatedAt: String,
)

/** R-24: response body of `GET /api/diagrams/{diagramId}/dsl`. */
@Serializable
public data class DiagramDslResponse(
    val dsl: String,
)

@Serializable
public data class CreateDiagramRequest(
    val name: String? = null,
)

@Serializable
public data class UpdateDiagramRequest(
    val name: String,
)

@Serializable
public data class UpdateProjectRequest(
    val name: String? = null,
    val description: String? = null,
)

@Serializable
public data class GenerateDiagramRequest(
    val prompt: String,
    val contextId: String? = null,
)

@Serializable
public data class GenerateDiagramResponse(
    val svg: String,
    val dsl: String,
    val generationId: String? = null,
)

/** R-8: success body for `POST /api/ai/context`. */
@Serializable
public data class AiContextUploadResponse(
    val contextId: String,
    val format: String,
    val characters: Int,
    val bytes: Int,
)

@Serializable
public data class UsageResponse(
    val plan: String,
    val projectsUsed: Long,
    val projectsLimit: Int? = null,
    val aiGenerationsThisMonth: Int,
    val aiGenerationsLimit: Int? = null,
    val aiLimitKind: String,
    val period: String,
)

@Serializable
public data class ErrorResponse(
    val message: String,
    val code: String? = null,
)

/** R-11: PATCH body for `/api/account/consent`. `null` = unveraendert lassen -- beide Schalter
 * sind unabhaengig voneinander aenderbar. */
@Serializable
public data class UpdateConsentRequest(
    val trainingOptOut: Boolean? = null,
    val showcaseOptIn: Boolean? = null,
)

/** R-23: PATCH body for `/api/account/profile`. `null` = unveraendert lassen. */
@Serializable
public data class UpdateProfileRequest(
    val displayName: String? = null,
)

/** R-10: metadata of one stored generation. Never carries svg/dsl/prompt -- the API doesn't
 * offer them. */
@Serializable
public data class GenerationSummaryResponse(
    val id: String,
    val diagramId: String,
    val createdAt: String,
    val rendererVersion: String,
    val reported: Boolean,
    val reportedAt: String? = null,
    val reportText: String? = null,
)

@Serializable
public data class ReportGenerationRequest(
    val text: String,
)

@Serializable
public data class CreateGenerationCommentRequest(
    val body: String,
)

@Serializable
public data class GenerationCommentResponse(
    val id: String,
    val authorRole: String,
    val authorDisplayName: String? = null,
    val body: String,
    val createdAt: String,
)

/** R-15: one day of [ActivityResponse.days]. */
@Serializable
public data class ActivityDayResponse(
    val date: String,
    val count: Long,
)

/** R-15: body of `GET /api/account/activity` -- the caller's own AI-generation activity calendar
 * (GitHub-contribution-graph shape, [dev.kuml.portal.web.activity.ActivityCalendar]). [days] is
 * always dense (one entry per UTC calendar day, zero-filled) and [firstDate] is always a Sunday,
 * but the window's END is always "today" (UTC) -- NEVER rounded forward to a Saturday, exactly
 * like GitHub's own contribution graph tolerates a partial trailing week. So [days].size is a
 * multiple of 7 only on days where "today" happens to land on a Saturday; on every other day it
 * is a few days more (see `GenerationActivityService`/`GenerationActivity`'s KDoc server-side).
 * Callers (this frontend's week-chunking, [dev.kuml.portal.web.activity.ActivityCalendar.weeks])
 * must NOT assume the last week is a full 7 columns. */
@Serializable
public data class ActivityResponse(
    val windowDays: Int,
    val firstDate: String,
    val lastDate: String,
    val days: List<ActivityDayResponse>,
    val totalGenerations: Long,
    val maxDayCount: Long,
    val activeDays: Int,
    val currentStreakDays: Int,
    val longestStreakDays: Int,
)

// ---------- R-21: Link-Sharing ----------

@Serializable
public data class CreateShareLinkRequest(
    val password: String? = null,
    val expiresAt: String? = null,
)

/** 201-Body von `POST /api/diagrams/{id}/share-links` -- [token] wird GENAU HIER, GENAU EINMAL
 * ausgeliefert. */
@Serializable
public data class IssuedShareLinkResponse(
    val id: String,
    val token: String,
    val createdAt: String,
    val expiresAt: String? = null,
    val passwordProtected: Boolean,
)

/** Listen-Element -- traegt NIE ein Token. */
@Serializable
public data class ShareLinkResponse(
    val id: String,
    val createdAt: String,
    val expiresAt: String? = null,
    val passwordProtected: Boolean,
    val lastViewedAt: String? = null,
)

/** Oeffentlicher Metadaten-Body von `GET /api/share/{token}`. */
@Serializable
public data class PublicShareResponse(
    val passwordRequired: Boolean,
    val diagramName: String? = null,
    val updatedAt: String? = null,
    val expiresAt: String? = null,
)

@Serializable
public data class UnlockShareRequest(
    val password: String,
)

@Serializable
public data class UnlockShareResponse(
    val viewToken: String,
    val expiresInSeconds: Long,
)

// ---------- R-5: Social Login ----------

@Serializable
public data class OAuthProvidersResponse(
    val providers: List<String>,
)

/** One row of `GET /api/account/oauth` -- never carries the provider-side subject id. */
@Serializable
public data class OAuthConnectionResponse(
    val provider: String,
    val email: String? = null,
    val createdAt: String,
    val lastLoginAt: String? = null,
)

// ---------- R-26: Persistent project-file store ----------

@Serializable
public data class ProjectFileResponse(
    val id: String,
    val projectId: String,
    val fileName: String,
    val kind: String,
    val contentType: String,
    val byteSize: Int,
    val description: String? = null,
    val createdAt: String,
    val updatedAt: String,
)

@Serializable
public data class UpdateProjectFileRequest(
    val description: String? = null,
)
